/**
 * **每个 Area 自己的视口记忆**（平移 / 缩放）。
 *
 * ## 为什么需要它
 *
 * Vue Flow 的 store 是按 Area id 建的，正常情况下 Area 只是 `v-show` 隐藏、
 * 不卸载，所以视口天然还在（`verify-area-spike` 里那条
 * 「v-show 往返后 viewport 保持」守的就是这个）。但有三种情况会重建 store：
 *
 *   1. **交换两个 Area 的内容** —— 节点图从左边搬到右边，组件卸载重挂；
 *   2. 换编辑器再换回来（比如节点图 → 输出 → 节点图）；
 *   3. 刷新页面。
 *
 * 前两种在同一个会话里发生，不记住的话表现是「我调好的视角没了」，
 * 而用户完全没做任何和视角有关的事。第三种顺手也覆盖了。
 *
 * ## ⚠️ 按 **Area id** 记，不按编辑器
 *
 * 「节点图」这个编辑器可以同时出现在两个 Area 上（多窗口），
 * 它们**各自**有自己的视口 —— 按编辑器记会让两个窗口互相打架。
 */
export interface Viewport {
  x: number
  y: number
  zoom: number
}

const STORAGE_KEY = 'node-print.viewports.v1'

/** 值合不合法。坏数据一律丢掉 —— 视口错了会让画布整个看不见 */
function isViewport(value: unknown): value is Viewport {
  if (!value || typeof value !== 'object') return false
  const v = value as { x?: unknown; y?: unknown; zoom?: unknown }
  return (
    Number.isFinite(v.x) &&
    Number.isFinite(v.y) &&
    Number.isFinite(v.zoom) &&
    (v.zoom as number) > 0
  )
}

/**
 * 从存储读出「Area id → 视口」。
 *
 * ⚠️ 和 `workspaces.ts` 同一个原则：**坏数据逐条丢**，
 * 不能让一条烂记录把整份记忆清空。
 */
export function loadViewportMemory(
  storage: Pick<Storage, 'getItem'> | null,
): Record<string, Viewport> {
  if (!storage) return {}
  let raw: unknown
  try {
    raw = JSON.parse(storage.getItem(STORAGE_KEY) ?? '{}')
  } catch {
    return {}
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}

  const out: Record<string, Viewport> = {}
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!id) continue
    if (!isViewport(value)) continue
    out[id] = { x: value.x, y: value.y, zoom: value.zoom }
  }
  return out
}

/** 写回去。配额满 / 隐私模式**静默吞掉** —— 记不住视口不该让界面报错 */
export function persistViewportMemory(
  storage: Pick<Storage, 'setItem'> | null,
  memory: Record<string, Viewport>,
): void {
  if (!storage) return
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(memory))
  } catch {
    /* 忽略 */
  }
}

/**
 * 两个视口是不是「一样」。
 *
 * ⚠️ 用一个小容差而不是 `===`：浮点视口每次滚动都会有极小的抖动，
 * 严格比较会让「这一帧和上一帧相同」永远不成立，
 * 于是每帧都要写一次存储。
 */
export function sameViewport(a: Viewport | undefined, b: Viewport, epsilon = 0.0005): boolean {
  if (!a) return false
  return (
    Math.abs(a.x - b.x) < epsilon &&
    Math.abs(a.y - b.y) < epsilon &&
    Math.abs(a.zoom - b.zoom) < epsilon
  )
}

/* ══════════════════════════════════════════════════════════════
   滚动位置
   ══════════════════════════════════════════════════════════════ */

/**
 * 某个 Area 里「哪个滚动容器停在哪」。
 *
 * ⚠️ `selector` 存的是**容器自己的第一个自有类名**（`np-` / `pg-` 前缀），
 * 不是一个稳定的 id。原因：这些滚动容器都是共享组件的内部元素
 * （`NpLogPanel` 的正文、属性面板的列表…），宿主拿不到它们的 ref，
 * 也不该为了记个滚动位置去给库组件加接口。
 *
 * 代价：**同一块 Area 里如果两个同类名容器都存在，只记得住第一个**。
 * 目前每种面板只有一个滚动容器，所以够用；将来真出现两个，
 * 应该改成给容器加 `data-scroll-key`，而不是继续靠类名。
 */
export interface ScrollMark {
  selector: string
  top: number
  left: number
}

/**
 * 从元素的类名里挑一个当锚点。
 *
 * 只认 `np-` / `pg-` 前缀的**自有**类名 —— 挑 `vue-flow__*` 或第三方类名
 * 会在换依赖版本时静静失配（表现成「滚动位置记不住了」，很难查）。
 * 挑不到就返回 `null`，调用方据此放弃这次记忆。
 */
export function scrollSelectorFor(classList: readonly string[]): string | null {
  return classList.find((name) => /^(np|pg)-/.test(name)) ?? null
}

/**
 * 清掉不再存在的 Area 的记忆。
 *
 * ⚠️ 不清理的话这份记录只增不减：预设反复切换、Area 反复开关，
 * 存储里会攒下一堆再也用不到的 id（而且老 id 可能被复用）。
 */
export function pruneViewportMemory(
  memory: Record<string, Viewport>,
  aliveIds: readonly string[],
): Record<string, Viewport> {
  const alive = new Set(aliveIds)
  const out: Record<string, Viewport> = {}
  for (const [id, vp] of Object.entries(memory)) {
    if (alive.has(id)) out[id] = vp
  }
  return out
}
